import {take, type Observable, type Subscription} from 'rxjs';
import {EventsRegister, registerEvent, registerEvents} from '../eventBus/register';

/**
 * 监听函数订阅记录。
 *
 * 说明：
 * RxJS Subject 本身不支持按原始 listener 删除，因此这里仅维护最小映射关系。
 */
const eventListenerSubscriptions = new Map<
    IEventBus.CadEventName,
    Map<IEventBus.CadEventListener, Subscription[]>
>();

/**
 * 读取事件 Subject。
 *
 * @param eventName 事件名称。
 * @returns 事件 Subject。
 */
const getEventSubject = (eventName: IEventBus.CadEventName): IEventBus.CadEventSubject => {
    const eventSubject = EventsRegister[eventName];

    // 事件必须先注册，避免拼错事件名称时静默失败。
    if (!eventSubject) {
        throw new Error(`事件未注册：${eventName}`);
    }

    return eventSubject;
};

/**
 * 记录监听函数与订阅对象的关系。
 *
 * @param eventName 事件名称。
 * @param listener 监听函数。
 * @param subscription 订阅对象。
 */
const recordEventSubscription = (
    eventName: IEventBus.CadEventName,
    listener: IEventBus.CadEventListener,
    subscription: Subscription,
): void => {
    const listenerMap = eventListenerSubscriptions.get(eventName)
        ?? new Map<IEventBus.CadEventListener, Subscription[]>();
    const subscriptions = listenerMap.get(listener) ?? [];

    subscriptions.push(subscription);
    listenerMap.set(listener, subscriptions);
    eventListenerSubscriptions.set(eventName, listenerMap);
};

/**
 * 清理监听函数与订阅对象的关系。
 *
 * @param eventName 事件名称。
 * @param listener 监听函数。
 * @param subscription 订阅对象。
 */
const removeEventSubscriptionRecord = (
    eventName: IEventBus.CadEventName,
    listener: IEventBus.CadEventListener,
    subscription: Subscription,
): void => {
    const listenerMap = eventListenerSubscriptions.get(eventName);
    const subscriptions = listenerMap?.get(listener);

    if (!listenerMap || !subscriptions) {
        return;
    }

    const nextSubscriptions = subscriptions.filter((item) => item !== subscription);
    if (nextSubscriptions.length > 0) {
        listenerMap.set(listener, nextSubscriptions);
        return;
    }

    listenerMap.delete(listener);
    if (listenerMap.size === 0) {
        eventListenerSubscriptions.delete(eventName);
    }
};

/**
 * 判断对象是否为 RxJS 订阅对象。
 *
 * @param value 待判断对象。
 * @returns 是否为订阅对象。
 */
const isSubscription = (value: unknown): value is Subscription => (
    typeof value === 'object'
    && value !== null
    && 'unsubscribe' in value
    && typeof (value as {unsubscribe: unknown}).unsubscribe === 'function'
);

/**
 * 创建事件操作方法集合。
 *
 * @returns 事件操作方法集合。
 */
export function useEvent(): IEventBus.UseEventMethods {
    return {
        add: useAddEvent,
        addOnce: useAddOnceEvent,
        dispatch: useDispatchEvent,
        observe: useObserveEvent,
        register: useRegisterEvent,
        registerMany: useRegisterEvents,
        remove: useRemoveEvent,
        removeAll: useRemoveAllEvent,
    };
}

/**
 * 注册单个事件。
 *
 * @param eventName 事件名称。
 */
export function useRegisterEvent(eventName: IEventBus.CadEventName): void {
    registerEvent(eventName);
}

/**
 * 批量注册事件。
 *
 * @param eventNames 事件名称列表。
 */
export function useRegisterEvents(eventNames: IEventBus.CadEventName[]): void {
    registerEvents(eventNames);
}

/**
 * 添加事件监听。
 *
 * @param eventName 事件名称。
 * @param listener 监听函数。
 * @returns RxJS 订阅对象。
 */
export function useAddEvent(
    eventName: IEventBus.CadEventName,
    listener: IEventBus.CadEventListener,
): Subscription {
    const subscription = getEventSubject(eventName).subscribe((payload) => {
        listener(...payload);
    });

    recordEventSubscription(eventName, listener, subscription);

    // 订阅被外部主动释放时，同步移除映射记录，避免 remove 时读到陈旧订阅。
    subscription.add(() => {
        removeEventSubscriptionRecord(eventName, listener, subscription);
    });

    return subscription;
}

/**
 * 添加只触发一次的事件监听。
 *
 * @param eventName 事件名称。
 * @param listener 监听函数。
 * @returns RxJS 订阅对象。
 */
export function useAddOnceEvent(
    eventName: IEventBus.CadEventName,
    listener: IEventBus.CadEventListener,
): Subscription {
    const subscription = getEventSubject(eventName).pipe(take(1)).subscribe((payload) => {
        listener(...payload);
    });

    recordEventSubscription(eventName, listener, subscription);
    subscription.add(() => {
        removeEventSubscriptionRecord(eventName, listener, subscription);
    });

    return subscription;
}

/**
 * 分发事件。
 *
 * @param eventName 事件名称。
 * @param payload 事件参数。
 */
export function useDispatchEvent(
    eventName: IEventBus.CadEventName,
    ...payload: IEventBus.CadEventPayload
): void {
    getEventSubject(eventName).next(payload);
}

/**
 * 读取事件 Observable。
 *
 * @param eventName 事件名称。
 * @returns 事件 Observable。
 */
export function useObserveEvent(
    eventName: IEventBus.CadEventName,
): Observable<IEventBus.CadEventPayload> {
    return getEventSubject(eventName).asObservable();
}

/**
 * 移除事件监听。
 *
 * @param eventName 事件名称。
 * @param listenerOrSubscription 监听函数或订阅对象。
 * @returns 是否移除了监听。
 */
export function useRemoveEvent(
    eventName: IEventBus.CadEventName,
    listenerOrSubscription: IEventBus.CadEventListener | Subscription,
): boolean {
    getEventSubject(eventName);

    if (isSubscription(listenerOrSubscription)) {
        const isActive = !listenerOrSubscription.closed;
        listenerOrSubscription.unsubscribe();
        return isActive;
    }

    const listenerMap = eventListenerSubscriptions.get(eventName);
    const subscriptions = listenerMap?.get(listenerOrSubscription);
    if (!subscriptions || subscriptions.length === 0) {
        return false;
    }

    [...subscriptions].forEach((subscription) => {
        subscription.unsubscribe();
    });

    return true;
}

/**
 * 移除指定事件的全部监听。
 *
 * @param eventName 事件名称。
 */
export function useRemoveAllEvent(eventName: IEventBus.CadEventName): void {
    getEventSubject(eventName);

    const listenerMap = eventListenerSubscriptions.get(eventName);
    if (!listenerMap) {
        return;
    }

    Array.from(listenerMap.values()).flat().forEach((subscription) => {
        subscription.unsubscribe();
    });
}
